import React, { useState, useContext, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Card, Typography, Avatar } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HistoryIcon from '@mui/icons-material/History';
import clsx from 'clsx';
import moment from 'moment';

import filter from 'common/filterVisitors';
import { AppContext } from 'AppContext';
import { ScannerEventContext } from 'ScannerEventContext';
import { checkVisitor } from 'common/desks';
import { BarcodeScannerComponent } from './BarcodeScannerComponent';

const PREFIX = 'AccessControlDesk';

const classes = {
  receptionContainer: `${PREFIX}-receptionContainer`,
  deskContainer: `${PREFIX}-deskContainer`,
  deskCard: `${PREFIX}-deskCard`,
  accessReject: `${PREFIX}-accessReject`,
  accessReject_allow: `${PREFIX}-accessReject_allow`,
  accessReject_deny: `${PREFIX}-accessReject_deny`,
  accessReject_repeat: `${PREFIX}-accessReject_repeat`,
  visitorInfoContainer: `${PREFIX}-visitorInfoContainer`,
  visitorInfo: `${PREFIX}-visitorInfo`,
  visitorInfo__name: `${PREFIX}-visitorInfo__name`,
  visitorInfo__group: `${PREFIX}-visitorInfo__group`,
  avatar: `${PREFIX}-avatar`,
  scannerContainer: `${PREFIX}-scannerContainer`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.receptionContainer}`]: {
    width: '100%',
    height: '100%',
  },

  [`&.${classes.deskContainer}`]: {
    width: '100%',
    height: '100%',
    zIndex: 1000000,
    paddingTop: 64,
    ['@media (max-width:700px)']: {
      paddingTop: 56,
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.deskCard}`]: {
    width: 500,
    height: 620,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    ['@media (max-width:700px)']: {
      width: '100%',
      height: '100%',
      borderRadius: 0,
    },
  },

  [`& .${classes.accessReject}`]: {
    width: '100%',
    height: 80,

    color: 'white',
    backgroundColor: 'grey',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  [`& .${classes.accessReject_allow}`]: {
    backgroundColor: '#4CAF50',
  },

  [`& .${classes.accessReject_deny}`]: {
    backgroundColor: '#E83C53',
  },

  [`& .${classes.accessReject_repeat}`]: {
    backgroundColor: '#FF9800',
  },

  [`& .${classes.visitorInfoContainer}`]: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  [`& .${classes.visitorInfo}`]: {
    flexGrow: 0,
  },

  [`& .${classes.visitorInfo__name}`]: {
    fontSize: 30,
    fontWeight: 500,
    textAlign: 'center',
    width: '100%',
    ['@media (max-width:700px)']: {
      fontSize: 22,
    },
  },

  [`& .${classes.visitorInfo__group}`]: {
    marginTop: 35,
    fontSize: 24,
    fontWeight: 400,
    textAlign: 'center',
    width: '100%',
    ['@media (max-width:700px)']: {
      marginTop: 15,
      fontSize: 18,
    },
  },

  [`& .${classes.avatar}`]: {
    width: 240,
    height: 240,
    margin: '0 auto 75px',
    ['@media (max-width:700px)']: {
      width: 160,
      height: 160,
      margin: '0 auto 30px',
    },
  },

  [`& .${classes.scannerContainer}`]: {
    width: 300,
    height: 300,
    margin: '0 auto 55px',
    backgroundColor: '#bdbdbd',
    borderRadius: 4,
    overflow: 'hidden',
    // видео камеры всегда вписываем в квадрат: на телефоне кадр вертикальный и иначе вылезает за рамку
    '& video': {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    ['@media (max-width:700px)']: {
      width: 220,
      height: 220,
      margin: '0 auto 20px',
      '& video': {
        objectFit: 'cover',
      },
    },
  },
}));

const AccessControlDesk = ({ receptionData, visitors }) => {
  const appContext = useContext(AppContext);
  const scannerContext = useContext(ScannerEventContext);
  const [currentVisitor, setCurrentVisitor] = useState();
  // allowed | denied | repeated
  const [status, setStatus] = useState();
  const [firstVisitAt, setFirstVisitAt] = useState();
  const [error, setError] = useState();

  // Держим таймер в ref: обычная переменная пересоздавалась на каждом рендере, поэтому
  // clearTimeout в setInfoTimer ничего не отменял и старый таймер гасил уже нового посетителя.
  const infoTimerRef = useRef();
  // номер последнего скана — чтобы ответ сервера на предыдущий скан не перетёр текущий
  const scanRef = useRef(0);

  async function handleControl(visitor) {
    const meetupId = receptionData.meetup.meetupId;
    const scan = ++scanRef.current;

    if (!checkVisitor(visitor, receptionData.deskSettings)) {
      showResult(visitor, 'denied');
      return;
    }

    if (!receptionData.deskSettings.repeatVisitControl) {
      appContext.visitorApi.writeDeskVisit(meetupId, visitor.visitorId);
      showResult(visitor, 'allowed');
      return;
    }

    let result;
    try {
      result = (await appContext.visitorApi.writeDeskVisit(meetupId, visitor.visitorId)).data;
    } catch (e) {
      console.error('writeDeskVisit failed', e);
    }
    if (scan !== scanRef.current) {
      return;
    }
    if (result && result.repeated) {
      showResult(visitor, 'repeated', result.firstVisitAt);
    } else {
      showResult(visitor, 'allowed');
    }
  }

  function showResult(visitor, newStatus, visitAt) {
    setCurrentVisitor(visitor);
    setStatus(newStatus);
    setFirstVisitAt(visitAt);
    setInfoTimer();
  }

  // Имя висит 5 секунд или до следующего пика сканера — на контроле доступа надо успеть
  // сверить человека с карточкой, а следующий скан сбрасывает отсчёт заново.
  function setInfoTimer() {
    clearTimeout(infoTimerRef.current);
    infoTimerRef.current = setTimeout(() => {
      setCurrentVisitor(null);
      setError(null);
    }, 5000);
  }

  const handleCodeFromScanner = code => {
    let fv = filter(visitors, 'code:' + code);
    if (fv.length === 1) {
      handleControl(fv[0]);
    } else {
      ++scanRef.current;
      // Без этого чужой или нечитаемый бейдж неотличим от неработающего сканера.
      setError('Посетитель не найден');
      setInfoTimer();
    }
  };

  // слушатель сканера регистрируется один раз, а вызывает актуальный обработчик (свежие visitors/настройки)
  const scannerHandlerRef = useRef(handleCodeFromScanner);
  scannerHandlerRef.current = handleCodeFromScanner;

  useEffect(() => {
    const listener = code => scannerHandlerRef.current(code);
    scannerContext.addListener(listener);

    return () => {
      scannerContext.removeListener(listener);
      clearTimeout(infoTimerRef.current);
    };
  }, []);

  const handleQrScan = code => {
    if (code) {
      handleCodeFromScanner(code);
    }
  };
  const handleQrError = err => {
    alert(err);
    setError(err);
    setInfoTimer();
  };

  return (
    <Root className={classes.deskContainer}>
      <Card className={classes.deskCard}>
        <div
          className={clsx({
            [classes.accessReject]: true,
            [classes.accessReject_allow]: currentVisitor && status === 'allowed',
            [classes.accessReject_deny]: currentVisitor && status === 'denied',
            [classes.accessReject_repeat]: currentVisitor && status === 'repeated',
          })}
        >
          <Typography style={{ color: 'inherit', fontSize: 20 }}>
            {!currentVisitor ? (
              <>
                {error ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <HighlightOffIcon style={{ marginRight: 5 }} />
                    <span>{error}</span>
                  </span>
                ) : (
                  'Ожидание сканирования'
                )}
              </>
            ) : status === 'allowed' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <CheckCircleOutlineIcon style={{ marginRight: 5 }} />
                <span>Доступ разрешен</span>
              </span>
            ) : status === 'repeated' ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <HistoryIcon style={{ marginRight: 5 }} />
                <span>Уже проходил{firstVisitAt ? ' в ' + moment(firstVisitAt).format('HH:mm') : ''}</span>
              </span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <HighlightOffIcon style={{ marginRight: 5 }} />
                Доступ запрещен
              </span>
            )}
          </Typography>
        </div>
        <div className={classes.visitorInfoContainer}>
          <div>
            {currentVisitor ? (
              <Avatar className={classes.avatar}>
                {currentVisitor && currentVisitor.photo && (
                  <img src={'/api/image/' + currentVisitor.photo} style={{ width: '100%' }} />
                )}
              </Avatar>
            ) : receptionData.deskSettings.webCamBarScannerEnabled ? (
              <div className={classes.scannerContainer}>
                <BarcodeScannerComponent
                  delay={300}
                  width='100%'
                  height='100%'
                  facingMode={receptionData.deskSettings.webCamNumber === 1 ? 'environment' : 'user'}
                  onUpdate={(err, result) => {
                    if (result) handleQrScan(result.text);
                    // else if (err) handleQrError(err)
                  }}
                />
              </div>
            ) : (
              <Avatar className={classes.avatar} />
            )}

            <Typography className={classes.visitorInfo__name}>
              {currentVisitor ? currentVisitor.firstName + ' ' + currentVisitor.lastName : 'Имя Фамилия'}
            </Typography>
            <Typography className={classes.visitorInfo__group}>
              {currentVisitor ? currentVisitor.groupName : 'Группа'}
            </Typography>
          </div>
        </div>
      </Card>
    </Root>
  );
};

export default AccessControlDesk;
